import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FaPaperclip, FaTimes, FaPlus, FaTrash } from 'react-icons/fa';
import {
  addPaymentVoucher,
  getPostingAccounts,
  getBanksAndSafes,
  getCostCenters,
  addCostCenter,
  getProjects,
  addProject,
  getCustomers,
  addCustomer,
  getTaxes,
  addTax,
  clearSuccess,
  clearError,
} from '../../../../redux/Slices/authSlice';
import i18n from '../../../../i18n/i18n';

const createEmptyLine = () => ({
  id: Date.now() + Math.random(),
  account_id: '',
  amount: '',
  description: '',
  cost_center_id: '',
});

const AddPaymentVoucher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n: i18nHook } = useTranslation('global');
  const isRTL = i18nHook.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';
  const {
    postingAccounts,
    costCenters,
    banksAndSafes: banksAndSafesData,
    costCentersPagination,
    projects, projectsPagination,
    customers, customersPagination,
    taxes, taxesPagination,
    isLoading,
    success,
    error,
  } = useSelector((state) => state.auth);

  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const [form, setForm] = useState({
    voucher_date: '',
    account_id: '',
    notes: '',
    reference: '',
    project_id: '',
    details: [createEmptyLine()],
  });

  const [attachments, setAttachments] = useState([]);
  const [mainDropdownOpen, setMainDropdownOpen] = useState(false);
  const [mainSearchTerm, setMainSearchTerm] = useState('');
  const mainSearchInputRef = useRef(null);

  const [detailDropdowns, setDetailDropdowns] = useState({});
  const [detailSearchTerms, setDetailSearchTerms] = useState({});
  const detailSearchInputRefs = useRef({});
  const lastErrorRef = useRef({ message: null, time: 0 });

  // Cost center dropdown states for each line
  const [costCenterDropdowns, setCostCenterDropdowns] = useState({});
  const [costCenterSearchTerms, setCostCenterSearchTerms] = useState({});
  const [costCenterCurrentPages, setCostCenterCurrentPages] = useState({});
  const costCenterSearchInputRefs = useRef({});
  const costCenterSearchTimeoutRefs = useRef({});

  // Cost center modal state
  const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);
  const [costCenterForm, setCostCenterForm] = useState({
    name_ar: '',
    name_en: '',
    code: '',
  });
  const [isSubmittingCostCenter, setIsSubmittingCostCenter] = useState(false);
  const [costCenterModalLineIndex, setCostCenterModalLineIndex] = useState(null);

  // Projects dropdown state
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectCurrentPage, setProjectCurrentPage] = useState(1);
  const projectSearchInputRef = useRef(null);
  const projectSearchTimeoutRef = useRef(null);

  // Project Modal Dropdowns State
  const [projModalCustomerDropdownOpen, setProjModalCustomerDropdownOpen] = useState(false);
  const [projModalCustomerSearchTerm, setProjModalCustomerSearchTerm] = useState('');
  const [projModalCustomerCurrentPage, setProjModalCustomerCurrentPage] = useState(1);
  const projModalCustomerSearchInputRef = useRef(null);
  const projModalCustomerSearchTimeoutRef = useRef(null);

  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [taxCurrentPage, setTaxCurrentPage] = useState(1);
  const taxSearchInputRef = useRef(null);
  const taxSearchTimeoutRef = useRef(null);

  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
  const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  // Modals state
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showAddTaxModal, setShowAddTaxModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  // Forms
  const [projectForm, setProjectForm] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    customer_id: null,
    start_date: '',
    end_date: '',
    status: 'planned',
    contract_value: '',
    tax_id: null,
    cost_center_id: null,
    notes: '',
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const projectNotesTextareaRef = useRef(null);

  const [taxForm, setTaxForm] = useState({
    name_ar: '',
    name_en: '',
    rate: '',
    is_active: true,
  });

  const [customerForm, setCustomerForm] = useState({
    name_en: '',
    name_ar: '',
    code: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    is_active: true,
    notes: ''
  });
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);
  const [isSubOperation, setIsSubOperation] = useState(false);
  const [costCenterSource, setCostCenterSource] = useState(null); // 'line' or 'project'

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setWindowWidth(window.innerWidth);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown-container]')) {
        setMainDropdownOpen(false);
        setProjectDropdownOpen(false);
        setDetailDropdowns({});
        setCostCenterDropdowns({});
        // Project modal dropdowns
        setProjModalCustomerDropdownOpen(false);
        setTaxDropdownOpen(false);
        setCostCenterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    dispatch(getPostingAccounts());
    dispatch(getBanksAndSafes());
    // Fetch large number to ensure we hit the cache for offline and have all items for dropdown
    dispatch(getCostCenters({ page: 1, per_page: 1000 }));
  }, [dispatch]);

  // Debounce search for cost centers
  useEffect(() => {
    Object.keys(costCenterSearchTerms).forEach((idx) => {
      const searchTerm = costCenterSearchTerms[idx];
      if (costCenterSearchTimeoutRefs.current[idx]) {
        clearTimeout(costCenterSearchTimeoutRefs.current[idx]);
      }
      costCenterSearchTimeoutRefs.current[idx] = setTimeout(() => {
        const page = costCenterCurrentPages[idx] || 1;
        setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: 1 }));
        // If searching, we query the API (which will be cached if previously fetched with same query, but unlikely)
        // For offline search, we ideally need client-side filtering of the large list if API is unreachable.
        // However, standard pattern here is just making the request.
        // If offline, request manager returns cached list if available (generic cache).
        // But parameters must match. Ideally we pre-fetch the full list and client-side filter?
        // Let's at least bump the per_page for the search too so if they search broadly it might hit?
        // Actually, best to just keep search flexible, but the initial load (lines 100-101) is key.
        dispatch(getCostCenters({ page: 1, per_page: 1000, search: searchTerm || undefined }));
      }, 300);
    });
    return () => {
      Object.values(costCenterSearchTimeoutRefs.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [costCenterSearchTerms, dispatch]);

  // Fetch cost centers when page changes
  useEffect(() => {
    Object.keys(costCenterCurrentPages).forEach((idx) => {
      const page = costCenterCurrentPages[idx] || 1;
      const search = costCenterSearchTerms[idx] || '';
      dispatch(getCostCenters({ page, per_page: 1000, search: search || undefined }));
    });
  }, [dispatch, costCenterCurrentPages]);

  // Projects fetching
  useEffect(() => {
    if (projectDropdownOpen) {
      dispatch(getProjects({ page: projectCurrentPage, per_page: 10, search: projectSearchTerm || undefined }));
    }
  }, [dispatch, projectCurrentPage, projectDropdownOpen, projectSearchTerm]);

  // Customer fetching (for Project Modal)
  useEffect(() => {
    if (projModalCustomerDropdownOpen) {
      dispatch(getCustomers({ page: projModalCustomerCurrentPage, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
    }
  }, [dispatch, projModalCustomerCurrentPage, projModalCustomerDropdownOpen, projModalCustomerSearchTerm]);

  // Taxes fetching
  useEffect(() => {
    if (taxDropdownOpen) {
      dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined }));
    }
  }, [dispatch, taxCurrentPage, taxDropdownOpen, taxSearchTerm]);

  // Cost Centers fetching (for Project Modal)
  useEffect(() => {
    if (costCenterDropdownOpen) {
      dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined }));
    }
  }, [dispatch, costCenterCurrentPage, costCenterDropdownOpen, costCenterSearchTerm]);

  useEffect(() => {
    if (success) {
      if (showAddCostCenterModal || showAddProjectModal || showAddTaxModal || showAddCustomerModal || isSubOperation) {
        dispatch(clearSuccess());
        setIsSubOperation(false);
        return;
      }
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      navigate('/payment-vouchers');
    }
  }, [success, dispatch, navigate, isRTL, showAddCostCenterModal, showAddProjectModal, showAddTaxModal, showAddCustomerModal, isSubOperation]);

  useEffect(() => {
    if (error) {
      if (showAddCostCenterModal || showAddProjectModal || showAddTaxModal || showAddCustomerModal || isSubOperation) {
        dispatch(clearError());
        setIsSubOperation(false);
        return;
      }
      const now = Date.now();
      const last = lastErrorRef.current;
      const errorMessage = typeof error === 'string' ? error : (error?.message || t('common.error'));
      if (!last.message || last.message !== errorMessage || now - last.time > 2000) {
        toast.error(errorMessage, { rtl: isRTL });
        lastErrorRef.current = { message: errorMessage, time: now };
      }
      dispatch(clearError());
    }
  }, [error, dispatch, isRTL, t, showAddCostCenterModal, showAddProjectModal, showAddTaxModal, showAddCustomerModal, isSubOperation]);

  const totalAmount = useMemo(
    () =>
      form.details.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0),
    [form.details]
  );

  const filteredPostingAccounts = useMemo(() => {
    const term = (mainSearchTerm || '').toLowerCase().trim();
    if (!term) return postingAccounts || [];
    return (postingAccounts || []).filter((acc) => {
      const code = (acc.code || '').toLowerCase();
      const nameAr = (acc.name_ar || '').toLowerCase();
      const nameEn = (acc.name_en || acc.name || '').toLowerCase();
      return code.includes(term) || nameAr.includes(term) || nameEn.includes(term);
    });
  }, [postingAccounts, mainSearchTerm, isRTL]);

  const getFilteredAccountsForLine = (idx) => {
    const term = (detailSearchTerms[idx] || '').toLowerCase().trim();
    if (!term) return postingAccounts || [];
    return (postingAccounts || []).filter((acc) => {
      const code = (acc.code || '').toLowerCase();
      const nameAr = (acc.name_ar || '').toLowerCase();
      const nameEn = (acc.name_en || acc.name || '').toLowerCase();
      return code.includes(term) || nameAr.includes(term) || nameEn.includes(term);
    });
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const mapped = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      url: URL.createObjectURL(file),
    }));

    setAttachments((prev) => [...prev, ...mapped]);
    event.target.value = '';
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const target = prev.find((att) => att.id === id);
      if (target?.url) {
        URL.revokeObjectURL(target.url);
      }
      return prev.filter((att) => att.id !== id);
    });
  };

  const updateLine = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      details: prev.details.map((line, lineIdx) =>
        lineIdx === idx ? { ...line, [field]: value } : line
      ),
    }));
  };

  const addDetailLine = () => {
    setForm((prev) => ({
      ...prev,
      details: [...prev.details, createEmptyLine()],
    }));
  };

  const removeDetailLine = (idx) => {
    setForm((prev) => {
      if (prev.details.length === 1) {
        return prev;
      }
      return {
        ...prev,
        details: prev.details.filter((_, lineIdx) => lineIdx !== idx),
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!form.voucher_date) {
      toast.error(t('paymentVouchers.form.dateRequired'), { rtl: isRTL });
      return;
    }

    if (!form.account_id) {
      toast.error(t('paymentVouchers.form.accountRequired'), { rtl: isRTL });
      return;
    }

    const detailsArray = form.details
      .map((line) => ({
        account_id: Number(line.account_id),
        amount: parseFloat(line.amount) || 0,
        description: line.description || '',
        cost_center_id: line.cost_center_id ? Number(line.cost_center_id) : null,
      }))
      .filter((line) => line.account_id && line.amount > 0);

    if (detailsArray.length === 0) {
      toast.error(t('paymentVouchers.form.detailsRequired'), { rtl: isRTL });
      return;
    }

    const total = detailsArray.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0);
    if (total <= 0) {
      toast.error(t('paymentVouchers.form.totalRequired'), { rtl: isRTL });
      return;
    }

    const payloadBase = {
      voucher_date: form.voucher_date,
      account_id: Number(form.account_id),
      total_amount: Number(total.toFixed(2)),
      notes: form.notes || '',
      reference: form.reference || '',
      project_id: form.project_id ? Number(form.project_id) : null,
      details: detailsArray,
    };

    if (attachments.length > 0) {
      const formData = new FormData();
      formData.append('voucher_date', payloadBase.voucher_date);
      formData.append('account_id', payloadBase.account_id);
      formData.append('total_amount', payloadBase.total_amount);
      formData.append('notes', payloadBase.notes);
      if (payloadBase.reference) {
        formData.append('reference', payloadBase.reference);
      }
      if (payloadBase.project_id) {
        formData.append('project_id', payloadBase.project_id);
      }
      formData.append('details', JSON.stringify(detailsArray));

      attachments.forEach((attachment, idx) => {
        formData.append(`attachments[${idx}]`, attachment.file);
      });

      dispatch(addPaymentVoucher(formData));
    } else {
      dispatch(addPaymentVoucher(payloadBase));
    }
  };

  const renderAccountDisplay = (accountId) => {
    if (!accountId) return t('paymentVouchers.form.selectAccount');

    // Check in postingAccounts
    let selected = (postingAccounts || []).find(
      (acc) => String(acc.id) === String(accountId)
    );

    // Also check in banksAndSafes
    if (!selected) {
      selected = (banksAndSafesData || []).find(
        (acc) => String(acc.id) === String(accountId)
      );
    }

    if (!selected) return t('paymentVouchers.form.selectAccount');
    const accountName = isRTL ? (selected.name_ar || selected.name || '') : (selected.name_en || selected.name || selected.name_ar || '');
    return accountName || '';
  };

  const costCenterOptions = useMemo(() => {
    if (!Array.isArray(costCenters)) return [];
    return costCenters;
  }, [costCenters]);

  const getFilteredCostCentersForLine = (idx) => {
    const term = (costCenterSearchTerms[idx] || '').toLowerCase().trim();
    if (!term) return costCenterOptions;
    return costCenterOptions.filter((cc) => {
      const code = (cc.code || '').toLowerCase();
      const nameAr = (cc.name_ar || '').toLowerCase();
      const nameEn = (cc.name_en || cc.name || '').toLowerCase();
      return code.includes(term) || nameAr.includes(term) || nameEn.includes(term);
    });
  };

  const renderCostCenterDisplay = (costCenterId) => {
    if (!costCenterId) return t('paymentVouchers.form.noCostCenter');
    const selected = (costCenters || []).find((cc) => String(cc.id) === String(costCenterId));
    if (!selected) return t('paymentVouchers.form.noCostCenter');
    const costCenterName = isRTL ? (selected.name_ar || selected.name || '') : (selected.name_en || selected.name || selected.name_ar || '');
    return `${selected.code ? `${selected.code} - ` : ''}${costCenterName}`;
  };

  const renderProjectDisplay = (projectId) => {
    if (!projectId) return t('quotations.add.selectProject', { defaultValue: 'اختر مشروعاً' });
    const selected = (projects || []).find((p) => String(p.id) === String(projectId));
    if (!selected) return t('quotations.add.selectProject', { defaultValue: 'اختر مشروعاً' });
    return isRTL ? (selected.name_ar || selected.name_en || selected.name) : (selected.name_en || selected.name_ar || selected.name);
  };

  // Project search debounce
  useEffect(() => {
    if (projectSearchTimeoutRef.current) clearTimeout(projectSearchTimeoutRef.current);
    projectSearchTimeoutRef.current = setTimeout(() => {
      if (projectDropdownOpen) {
        dispatch(getProjects({ page: 1, per_page: 10, search: projectSearchTerm || undefined }));
        setProjectCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(projectSearchTimeoutRef.current);
  }, [projectSearchTerm, dispatch, projectDropdownOpen]);

  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    setProjectForm(prev => ({ ...prev, [name]: value }));
  };

  const handleTaxFormChange = (e) => {
    const { name, value } = e.target;
    setTaxForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomerFormChange = (e) => {
    const { name, value } = e.target;
    setCustomerForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!projectForm.name_ar || !projectForm.customer_id) {
      toast.error(t('quotations.add.modals.addProject.required'), { rtl: isRTL });
      return;
    }

    try {
      setIsSubmittingProject(true);
      setIsSubOperation(true);
      const payload = {
        ...projectForm,
        customer_id: Number(projectForm.customer_id),
        tax_id: projectForm.tax_id ? Number(projectForm.tax_id) : null,
        cost_center_id: projectForm.cost_center_id ? Number(projectForm.cost_center_id) : null,
      };

      const result = await dispatch(addProject(payload)).unwrap();
      if (result?.data?.id) {
        setForm(prev => ({ ...prev, project_id: result.data.id }));
        await dispatch(getProjects({ page: 1, per_page: 10 }));
        setShowAddProjectModal(false);
        setProjectForm({ code: '', name_ar: '', name_en: '', customer_id: null, start_date: '', end_date: '', status: 'planned', contract_value: '', tax_id: null, cost_center_id: null, notes: '' });
        toast.success(t('quotations.add.modals.addProject.success'), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error?.message || t('common.error'), { rtl: isRTL });
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const handleAddTax = async (e) => {
    e.preventDefault();
    try {
      setIsSubmittingProject(true); // Re-using project submit state or could add isSubmittingTax
      setIsSubOperation(true);
      const payload = { ...taxForm, rate: Number(taxForm.rate) };
      const result = await dispatch(addTax(payload)).unwrap();
      if (result?.data?.id) {
        setProjectForm(prev => ({ ...prev, tax_id: result.data.id }));
        await dispatch(getTaxes({ page: 1, per_page: 10 }));
        setShowAddTaxModal(false);
        setTaxForm({ name_ar: '', name_en: '', rate: '', type: 'percentage' });
        toast.success(t('quotations.add.modals.addTax.success'), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error?.message || t('common.error'), { rtl: isRTL });
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      setIsSubmittingCustomer(true);
      setIsSubOperation(true);
      const result = await dispatch(addCustomer({ ...customerForm, is_active: customerForm.is_active ? 1 : 0 })).unwrap();
      if (result) {
        setProjectForm(prev => ({ ...prev, customer_id: result.data.id }));
        await dispatch(getCustomers({ page: 1, per_page: 10 }));
        setShowAddCustomerModal(false);
        setCustomerForm({ name_en: '', name_ar: '', code: '', contact_person: '', email: '', phone: '', address: '', tax_number: '', is_active: true, notes: '' });
        toast.success(t('customers.add.success'), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error?.message || t('common.error'), { rtl: isRTL });
    } finally {
      setIsSubmittingCustomer(false);
    }
  };

  const handleCostCenterFormChange = (e) => {
    const { name, value } = e.target;
    setCostCenterForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCostCenter = async (e) => {
    e.preventDefault();
    if (!costCenterForm.name_ar) {
      toast.error(t('paymentVouchers.form.modals.addCostCenter.requiredFields'), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingCostCenter(true);
      setIsSubOperation(true);
      const payload = {
        name_ar: costCenterForm.name_ar,
        name_en: costCenterForm.name_en || undefined,
        code: costCenterForm.code || undefined,
      };
      const result = await dispatch(addCostCenter(payload)).unwrap();
      if (result?.data?.id) {
        // Set the newly added cost center in the line if modal was opened from a specific line
        if (costCenterModalLineIndex !== null) {
          updateLine(costCenterModalLineIndex, 'cost_center_id', result.data.id);
        }
        // Refresh cost centers list for all lines
        Object.keys(costCenterCurrentPages).forEach((idx) => {
          const page = costCenterCurrentPages[idx] || 1;
          const search = costCenterSearchTerms[idx] || '';
          dispatch(getCostCenters({ page, per_page: 10, search: search || undefined }));
        });
        setShowAddCostCenterModal(false);
        setCostCenterForm({
          name_ar: '',
          name_en: '',
          code: '',
        });
        setCostCenterModalLineIndex(null);
        toast.success(t('paymentVouchers.form.modals.addCostCenter.success'), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || t('paymentVouchers.form.modals.addCostCenter.error'), { rtl: isRTL });
    } finally {
      setIsSubmittingCostCenter(false);
    }
  };
  const banksAndSafes = useMemo(() => {
    return Array.isArray(banksAndSafesData) ? banksAndSafesData : [];
  }, [banksAndSafesData]);
  const filteredBanksAndSafes = useMemo(() => {
    const term = (mainSearchTerm || '').toLowerCase().trim();
    if (!term) return banksAndSafes;
    return banksAndSafes.filter((acc) => {
      const code = (acc.code || '').toLowerCase();
      const nameAr = (acc.name_ar || '').toLowerCase();
      const nameEn = (acc.name_en || acc.name || '').toLowerCase();
      const name = isRTL ? nameAr : nameEn;
      return code.includes(term) || nameAr.includes(term) || nameEn.includes(term);
    });
  }, [banksAndSafes, mainSearchTerm, isRTL]);

  const isTablet = windowWidth <= 992;
  const isMobile = windowWidth <= 768;

  return (
    <div
      style={{
        padding: isMobile ? '16px' : '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        boxSizing: 'border-box',
        direction: dir
      }}
    >
      <h1 style={{ fontSize: isMobile ? '18px' : '20px', marginBottom: isMobile ? '12px' : '16px', textAlign }}>
        {t('paymentVouchers.form.title')}
      </h1>
      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: '#1b3350',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: isMobile ? '16px' : '20px',
          width: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          pointerEvents: (isLoading || success) ? 'none' : 'auto',
          opacity: (isLoading || success) ? 0.7 : 1
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          <div>
            <label style={{ display: 'block', marginBottom: 6, textAlign }}>{t('paymentVouchers.form.voucherDate')}</label>
            <input
              type="date"
              value={form.voucher_date}
              onChange={(e) => setForm((prev) => ({ ...prev, voucher_date: e.target.value }))}
              required
              style={{
                width: '100%',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                color: 'white',
                borderRadius: 8,
                padding: '10px',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, textAlign }}>{t('paymentVouchers.form.reference')}</label>
            <input
              type="text"
              value={form.reference}
              onChange={(e) => setForm((prev) => ({ ...prev, reference: e.target.value }))}
              style={{
                width: '100%',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                color: 'white',
                borderRadius: 8,
                padding: '10px',
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }} data-dropdown-container>
          <label style={{ display: 'block', marginBottom: 6, textAlign }}>{t('paymentVouchers.form.from', { defaultValue: 'From' })}</label>
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => {
                setMainDropdownOpen((prev) => !prev);
                setTimeout(() => {
                  if (mainSearchInputRef.current) {
                    mainSearchInputRef.current.focus();
                  }
                }, 100);
              }}
              style={{
                width: '100%',
                padding: '8px 10px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxSizing: 'border-box',
                fontSize: '14px',
              }}
            >
              <span>{renderAccountDisplay(form.account_id)}</span>
              <span style={{ fontSize: '12px' }}>▼</span>
            </div>
            {mainDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '0 0 8px 8px',
                  zIndex: 1000,
                  maxHeight: '400px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  ref={mainSearchInputRef}
                  type="text"
                  placeholder={t('paymentVouchers.form.searchAccount')}
                  value={mainSearchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setMainSearchTerm(value);
                    if (value) {
                      dispatch(getPostingAccounts(value));
                    } else {
                      dispatch(getPostingAccounts());
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#2d3748',
                    border: 'none',
                    borderBottom: '1px solid #333',
                    color: 'white',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    direction: dir,
                  }}
                  dir={dir}
                />
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {filteredBanksAndSafes.length > 0 ? (
                    filteredBanksAndSafes.map((acc) => {
                      const isSelected = String(form.account_id) === String(acc.id);
                      return (
                        <div
                          key={acc.id}
                          onClick={() => {
                            if (isSelected) return;
                            setForm((prev) => ({ ...prev, account_id: acc.id }));
                            setMainDropdownOpen(false);
                            setMainSearchTerm('');
                          }}
                          style={{
                            padding: '10px',
                            cursor: isSelected ? 'not-allowed' : 'pointer',
                            backgroundColor: isSelected ? '#2a2a2a' : 'transparent',
                            color: isSelected ? '#888' : 'white',
                            borderBottom: '1px solid #333',
                            opacity: isSelected ? 0.6 : 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.target.style.backgroundColor = '#374151';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.target.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <span>
                            {acc.code ? `${acc.code} - ` : ''}
                            {isRTL ? (acc.name_ar || acc.name_en || acc.name) : (acc.name_en || acc.name_ar || acc.name)}
                          </span>
                          {isSelected && (
                            <span style={{ fontSize: '12px', color: '#f59f00' }}>
                              {t('receiptVouchers.add.accountAlreadySelected', { defaultValue: 'This account is already selected' })}
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: '10px', color: '#888', textAlign: 'center', direction: dir }}>{t('common.noResults', { defaultValue: 'No results found' })}</div>
                  )}
                </div>
              </div>
            )}
            {mainDropdownOpen && (
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                onClick={() => setMainDropdownOpen(false)}
              />
            )}
          </div>
        </div>

        <div style={{ marginBottom: '16px' }} data-dropdown-container>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ textAlign }}>{t('paymentVouchers.form.project', { defaultValue: 'المشروع' })}</label>
            <button
              type="button"
              onClick={() => setShowAddProjectModal(true)}
              style={{
                backgroundColor: '#AC2000',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '4px 10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                fontWeight: '600'
              }}
            >
              <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <div
              data-dropdown-toggle
              onClick={() => {
                setMainDropdownOpen(false);
                setProjectDropdownOpen((prev) => !prev);
                setTimeout(() => {
                  if (projectSearchInputRef.current) {
                    projectSearchInputRef.current.focus();
                  }
                }, 100);
              }}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxSizing: 'border-box',
                fontSize: '14px',
              }}
            >
              <span>
                {form.project_id
                  ? (projects || []).find((p) => String(p.id) === String(form.project_id))
                    ? `${(projects || []).find((p) => String(p.id) === String(form.project_id)).code ? `${(projects || []).find((p) => String(p.id) === String(form.project_id)).code} - ` : ''}${isRTL ? ((projects || []).find((p) => String(p.id) === String(form.project_id)).name_ar || (projects || []).find((p) => String(p.id) === String(form.project_id)).name_en) : ((projects || []).find((p) => String(p.id) === String(form.project_id)).name_en || (projects || []).find((p) => String(p.id) === String(form.project_id)).name_ar)}`
                    : t('paymentVouchers.form.selectProject', { defaultValue: 'Select Project' })
                  : t('paymentVouchers.form.selectProject', { defaultValue: 'Select Project' })}
              </span>
              <span style={{ fontSize: '12px' }}>▼</span>
            </div>
            {projectDropdownOpen && (
              <DropdownWithSearch
                isOpen={projectDropdownOpen}
                onToggle={setProjectDropdownOpen}
                searchTerm={projectSearchTerm}
                onSearchChange={setProjectSearchTerm}
                searchInputRef={projectSearchInputRef}
                items={projects || []}
                pagination={projectsPagination}
                currentPage={projectCurrentPage}
                onPageChange={setProjectCurrentPage}
                onSelect={(id) => {
                  setForm((prev) => ({ ...prev, project_id: id }));
                  setProjectDropdownOpen(false);
                }}
                label={t('paymentVouchers.form.project', { defaultValue: 'المشروع' })}
              />
            )}
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: 6, textAlign }}>{t('paymentVouchers.form.notes')}</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            rows={3}
            style={{
              width: '100%',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              color: 'white',
              borderRadius: 8,
              padding: '10px',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginTop: '16px', width: '100%' }}>
          {form.details.map((line, idx) => {
            const filteredAccounts = getFilteredAccountsForLine(idx);
            return (
              <div key={line.id} style={{ marginBottom: '16px', width: '100%' }}>
                {/* Headers Row */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                      ? '1fr'
                      : isTablet
                        ? 'repeat(2, minmax(0, 1fr))'
                        : 'minmax(0, 1.3fr) minmax(0, 1.1fr) minmax(0, 1.5fr) minmax(0, 1.5fr) 50px',
                    gap: '10px',
                    marginBottom: '6px',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <label style={{ fontSize: '14px', color: '#9ca3af', textAlign, fontWeight: '500' }}>
                    {idx === 0 ? t('paymentVouchers.form.to', { defaultValue: 'To' }) : ''}
                  </label>
                  <div></div>
                  <div></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between' }}>
                    <label style={{ fontSize: '14px', color: '#9ca3af', textAlign, fontWeight: '500' }}>
                      {t('paymentVouchers.form.costCenter')}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCostCenterModalLineIndex(idx);
                        setShowAddCostCenterModal(true);
                      }}
                      style={{
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        padding: '4px 6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        flexShrink: 0,
                      }}
                      title={t('paymentVouchers.form.addCostCenter')}
                    >
                      <FaPlus style={{ fontSize: '12px' }} />
                    </button>
                  </div>
                  <div></div>
                </div>
                {/* Inputs Row */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                      ? '1fr'
                      : isTablet
                        ? 'repeat(2, minmax(0, 1fr))'
                        : 'minmax(0, 1.3fr) minmax(0, 1.1fr) minmax(0, 1.5fr) minmax(0, 1.5fr) 50px',
                    gap: '10px',
                    alignItems: 'start',
                    width: '100%',
                  }}
                >
                  <div style={{ position: 'relative' }} data-dropdown-container>
                    <div
                      onClick={() => {
                        setDetailDropdowns((prev) => ({
                          ...prev,
                          [idx]: !prev[idx],
                        }));
                        setTimeout(() => {
                          if (detailSearchInputRefs.current[idx]) {
                            detailSearchInputRefs.current[idx].focus();
                          }
                        }, 100);
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        fontSize: '14px',
                      }}
                    >
                      <span>{renderAccountDisplay(line.account_id)}</span>
                      <span style={{ fontSize: '12px' }}>▼</span>
                    </div>
                    {detailDropdowns[idx] && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: '0 0 8px 8px',
                          zIndex: 1000,
                          maxHeight: '400px',
                          overflowY: 'auto',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          ref={(el) => {
                            detailSearchInputRefs.current[idx] = el;
                          }}
                          type="text"
                          placeholder={t('paymentVouchers.form.searchAccount')}
                          value={detailSearchTerms[idx] || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDetailSearchTerms((prev) => ({ ...prev, [idx]: value }));
                            if (value) {
                              dispatch(getPostingAccounts(value));
                            } else {
                              dispatch(getPostingAccounts());
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#2d3748',
                            border: 'none',
                            borderBottom: '1px solid #333',
                            color: 'white',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            direction: dir,
                          }}
                          dir={dir}
                        />
                        <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                          {filteredAccounts.length > 0 ? (
                            filteredAccounts.map((acc) => {
                              const isSelectedInOtherLine = form.details.some((detailItem, detailIdx) => {
                                if (detailIdx === idx) return false;
                                const otherId = detailItem.account_id ? String(detailItem.account_id) : '';
                                return otherId !== '' && otherId === String(acc.id);
                              });
                              const isSelected = String(line.account_id) === String(acc.id);
                              const isDisabled = isSelectedInOtherLine;
                              const accountName = isRTL ? (acc.name_ar || acc.name || '') : (acc.name_en || acc.name || acc.name_ar || '');
                              return (
                                <div
                                  key={acc.id}
                                  onClick={() => {
                                    if (isDisabled) return;
                                    updateLine(idx, 'account_id', acc.id);
                                    setDetailDropdowns((prev) => ({ ...prev, [idx]: false }));
                                    setDetailSearchTerms((prev) => ({ ...prev, [idx]: '' }));
                                  }}
                                  style={{
                                    padding: '10px',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    backgroundColor: isSelected
                                      ? '#AC2000'
                                      : isDisabled
                                        ? '#2a2a2a'
                                        : 'transparent',
                                    color: isDisabled ? '#888' : 'white',
                                    borderBottom: '1px solid #333',
                                    opacity: isDisabled ? 0.6 : 1,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isDisabled && !isSelected) {
                                      e.target.style.backgroundColor = '#374151';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isDisabled && !isSelected) {
                                      e.target.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                >
                                  <span>
                                    {acc.code ? `${acc.code} - ` : ''}
                                    {accountName}
                                  </span>
                                  {isDisabled && (
                                    <span style={{ fontSize: '12px', color: '#f59f00' }}>
                                      {t('paymentVouchers.form.accountAlreadySelected')}
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div style={{ padding: '10px', color: '#888', textAlign: 'center' }}>{t('paymentVouchers.form.noResults')}</div>
                          )}
                        </div>
                      </div>
                    )}
                    {detailDropdowns[idx] && (
                      <div
                        style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                        onClick={() => {
                          setDetailDropdowns((prev) => ({ ...prev, [idx]: false }));
                        }}
                      />
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"

                    placeholder={t('paymentVouchers.form.amount')}
                    value={line.amount}
                    onChange={(e) => updateLine(idx, 'amount', e.target.value)}
                    style={{
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      color: 'white',
                      borderRadius: 8,
                      padding: '6px 10px',
                      width: '100%',
                      boxSizing: 'border-box',
                      minWidth: 0,
                      fontSize: '14px',
                      height: '36px',
                    }}
                  />
                  <textarea
                    placeholder={t('paymentVouchers.form.description')}
                    value={line.description}
                    onChange={(e) => updateLine(idx, 'description', e.target.value)}
                    style={{
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      color: 'white',
                      borderRadius: 8,
                      padding: '6px 10px',
                      width: '100%',
                      boxSizing: 'border-box',
                      minWidth: 0,
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      height: '36px',
                      minHeight: '36px',
                      lineHeight: '1.5',
                    }}
                  />
                  <div style={{ position: 'relative' }} data-dropdown-container>
                    <div
                      onClick={() => {
                        setCostCenterDropdowns((prev) => ({
                          ...prev,
                          [idx]: !prev[idx],
                        }));
                        if (!costCenterCurrentPages[idx]) {
                          setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: 1 }));
                        }
                        setTimeout(() => {
                          if (costCenterSearchInputRefs.current[idx]) {
                            costCenterSearchInputRefs.current[idx].focus();
                          }
                        }, 100);
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        fontSize: '14px',
                        height: '36px',
                      }}
                    >
                      <span>{renderCostCenterDisplay(line.cost_center_id)}</span>
                      <span style={{ fontSize: '12px' }}>▼</span>
                    </div>
                    {costCenterDropdowns[idx] && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: '0 0 8px 8px',
                          zIndex: 1000,
                          maxHeight: '400px',
                          overflowY: 'auto',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                          marginTop: 4,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          ref={(el) => {
                            costCenterSearchInputRefs.current[idx] = el;
                          }}
                          type="text"
                          placeholder={t('paymentVouchers.form.searchCostCenter')}
                          value={costCenterSearchTerms[idx] || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCostCenterSearchTerms((prev) => ({ ...prev, [idx]: value }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#2d3748',
                            border: 'none',
                            borderBottom: '1px solid #333',
                            color: 'white',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            direction: dir,
                          }}
                          dir={dir}
                        />
                        {costCentersPagination && costCentersPagination.last_page > 1 && (
                          <div style={{ padding: '8px', borderTop: '1px solid #333', borderBottom: '1px solid #333', background: '#14263b' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ color: '#ccc', fontSize: 11 }}>
                                الصفحة {costCentersPagination.current_page} من {costCentersPagination.last_page}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (costCentersPagination.current_page > 1) {
                                    setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: 1 }));
                                  }
                                }}
                                disabled={costCentersPagination.current_page === 1}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: costCentersPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                                  color: costCentersPagination.current_page === 1 ? '#666' : 'white',
                                  border: '1px solid #444',
                                  borderRadius: '4px',
                                  cursor: costCentersPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                                  fontSize: 10,
                                  fontWeight: 'bold',
                                  minWidth: '28px',
                                }}
                              >
                                ««
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (costCentersPagination.current_page > 1) {
                                    setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: costCentersPagination.current_page - 1 }));
                                  }
                                }}
                                disabled={costCentersPagination.current_page === 1}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: costCentersPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                                  color: costCentersPagination.current_page === 1 ? '#666' : 'white',
                                  border: '1px solid #444',
                                  borderRadius: '4px',
                                  cursor: costCentersPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                                  fontSize: 10,
                                  fontWeight: 'bold',
                                  minWidth: '28px',
                                }}
                              >
                                ‹
                              </button>
                              {(() => {
                                const pages = [];
                                const current = costCentersPagination.current_page;
                                const last = costCentersPagination.last_page;
                                const showPages = 5;
                                let start = Math.max(1, current - Math.floor(showPages / 2));
                                let end = Math.min(last, start + showPages - 1);
                                if (end - start < showPages - 1) {
                                  start = Math.max(1, end - showPages + 1);
                                }
                                for (let i = start; i <= end; i++) {
                                  pages.push(i);
                                }
                                return pages.map((page) => (
                                  <button
                                    key={page}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: page }));
                                    }}
                                    style={{
                                      padding: '4px 8px',
                                      backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                                      color: 'white',
                                      border: page === current ? '1px solid #AC2000' : '1px solid #444',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: 10,
                                      fontWeight: page === current ? 'bold' : 'normal',
                                      minWidth: '28px',
                                    }}
                                  >
                                    {page}
                                  </button>
                                ));
                              })()}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (costCentersPagination.current_page < costCentersPagination.last_page) {
                                    setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: costCentersPagination.current_page + 1 }));
                                  }
                                }}
                                disabled={costCentersPagination.current_page === costCentersPagination.last_page}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: costCentersPagination.current_page === costCentersPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                                  color: costCentersPagination.current_page === costCentersPagination.last_page ? '#666' : 'white',
                                  border: '1px solid #444',
                                  borderRadius: '4px',
                                  cursor: costCentersPagination.current_page === costCentersPagination.last_page ? 'not-allowed' : 'pointer',
                                  fontSize: 10,
                                  fontWeight: 'bold',
                                  minWidth: '28px',
                                }}
                              >
                                ›
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (costCentersPagination.current_page < costCentersPagination.last_page) {
                                    setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: costCentersPagination.last_page }));
                                  }
                                }}
                                disabled={costCentersPagination.current_page === costCentersPagination.last_page}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: costCentersPagination.current_page === costCentersPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                                  color: costCentersPagination.current_page === costCentersPagination.last_page ? '#666' : 'white',
                                  border: '1px solid #444',
                                  borderRadius: '4px',
                                  cursor: costCentersPagination.current_page === costCentersPagination.last_page ? 'not-allowed' : 'pointer',
                                  fontSize: 10,
                                  fontWeight: 'bold',
                                  minWidth: '28px',
                                }}
                              >
                                »»
                              </button>
                            </div>
                          </div>
                        )}
                        <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                          {(() => {
                            const filteredCC = getFilteredCostCentersForLine(idx);
                            return filteredCC.length > 0 ? (
                              filteredCC.map((cc) => (
                                <div
                                  key={cc.id}
                                  onClick={() => {
                                    updateLine(idx, 'cost_center_id', cc.id);
                                    setCostCenterDropdowns((prev) => ({ ...prev, [idx]: false }));
                                    setCostCenterSearchTerms((prev) => ({ ...prev, [idx]: '' }));
                                  }}
                                  style={{
                                    padding: '10px',
                                    cursor: 'pointer',
                                    backgroundColor: String(line.cost_center_id) === String(cc.id) ? '#AC2000' : 'transparent',
                                    color: 'white',
                                    borderBottom: '1px solid #333',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (String(line.cost_center_id) !== String(cc.id)) {
                                      e.target.style.backgroundColor = '#374151';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (String(line.cost_center_id) !== String(cc.id)) {
                                      e.target.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                >
                                  <span>
                                    {cc.code ? `${cc.code} - ` : ''}
                                    {isRTL ? (cc.name_ar || cc.name || '') : (cc.name_en || cc.name || cc.name_ar || '')}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '10px', color: '#888', textAlign: 'center' }}>{t('paymentVouchers.form.noResults')}</div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                    {costCenterDropdowns[idx] && (
                      <div
                        style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                        onClick={() => {
                          setCostCenterDropdowns((prev) => ({ ...prev, [idx]: false }));
                        }}
                      />
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', width: '100%', padding: 0 }}>
                    <button
                      type="button"
                      onClick={() => removeDetailLine(idx)}
                      disabled={form.details.length === 1}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px',
                        cursor: form.details.length === 1 ? 'not-allowed' : 'pointer',
                        opacity: form.details.length === 1 ? 0.6 : 1,
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        alignSelf: 'flex-start',
                      }}
                      title={t('paymentVouchers.form.delete')}
                    >
                      <FaTrash style={{ fontSize: '14px' }} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={addDetailLine}
            style={{
              background: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 14px',
              cursor: 'pointer',
            }}
          >
            + {t('paymentVouchers.form.addLine')}
          </button>
        </div>

        <div
          style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: '#1a1f2e',
            borderRadius: '8px',
            border: '1px solid #333',
            width: '100%',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr', gap: '16px', alignItems: 'center', width: '100%' }}>
            <div style={{ color: '#9ca3af', fontSize: '12px', textAlign, whiteSpace: 'nowrap' }}>{t('paymentVouchers.form.totalAmount')}</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', textAlign: isRTL ? 'right' : 'left', width: '100%' }}>
              {totalAmount.toFixed(2)}
            </div>
          </div>
        </div>

        <div style={{ marginTop: '24px', marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '15px',
              fontWeight: 'bold',
              color: 'white',
              fontSize: '16px',
              textAlign: isRTL ? 'right' : 'left',
            }}
          >
            {t('paymentVouchers.form.attachments')}
          </label>

          <div style={{ marginBottom: '20px' }}>
            <input
              type="file"
              id="paymentVoucherAttachments"
              multiple
              accept="*/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <label
              htmlFor="paymentVoucherAttachments"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'transparent',
                color: 'white',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                border: '2px solid #AC2000',
                transition: 'all 0.3s ease',
                textAlign: isRTL ? 'right' : 'left',
                justifyContent: isRTL ? 'flex-end' : 'flex-start',
                direction: isRTL ? 'rtl' : 'ltr',
                width: 'fit-content'
              }}
            >
              <FaPaperclip />
              {t('paymentVouchers.form.addAttachments')}
            </label>
          </div>

          {attachments.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 160 : 200}px, 1fr))`,
                gap: '15px',
                marginTop: '20px',
              }}
            >
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  style={{
                    backgroundColor: '#1a1f2e',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #333',
                    position: 'relative',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => removeAttachment(attachment.id)}
                    style={{
                      position: 'absolute',
                      top: '5px',
                      left: '5px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '25px',
                      height: '25px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                    }}
                  >
                    <FaTimes />
                  </button>
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={isRTL ? 'اضغط لفتح المرفق' : 'Click to open attachment'}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      textDecoration: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <FaPaperclip style={{ fontSize: '24px', color: '#AC2000' }} />
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'white',
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                      }}
                    >
                      {attachment.name}
                    </div>
                    <div style={{ fontSize: '10px', color: '#999' }}>
                      {(attachment.size / 1024).toFixed(2)} KB
                    </div>
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: '24px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => navigate('/payment-vouchers')}
            style={{
              background: '#666',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
            }}
          >
            {t('paymentVouchers.form.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              background: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? t('paymentVouchers.form.saving') : t('paymentVouchers.form.save')}
          </button>
        </div>
      </form>

      {/* Add Cost Center Modal */}
      {showAddCostCenterModal && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddCostCenterModal(false);
              setCostCenterModalLineIndex(null);
              setCostCenterForm({
                name_ar: '',
                name_en: '',
                code: '',
              });
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('paymentVouchers.form.modals.addCostCenter.title')}</h2>
              <button
                onClick={() => {
                  setShowAddCostCenterModal(false);
                  setCostCenterForm({
                    name_ar: '',
                    name_en: '',
                    code: '',
                  });
                  setCostCenterModalLineIndex(null);
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCostCenter}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('paymentVouchers.form.modals.addCostCenter.nameAr')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={costCenterForm.name_ar}
                    onChange={handleCostCenterFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('paymentVouchers.form.modals.addCostCenter.nameEn')}
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={costCenterForm.name_en}
                    onChange={handleCostCenterFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('paymentVouchers.form.modals.addCostCenter.code')}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={costCenterForm.code}
                    onChange={handleCostCenterFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCostCenterModal(false);
                    setCostCenterForm({
                      name_ar: '',
                      name_en: '',
                      code: '',
                    });
                    setCostCenterModalLineIndex(null);
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  {t('paymentVouchers.form.modals.addCostCenter.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingCostCenter}
                  style={{
                    backgroundColor: isSubmittingCostCenter ? '#666' : '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: isSubmittingCostCenter ? 'not-allowed' : 'pointer',
                    opacity: isSubmittingCostCenter ? 0.7 : 1
                  }}
                >
                  {isSubmittingCostCenter ? t('paymentVouchers.form.modals.addCostCenter.saving') : t('paymentVouchers.form.modals.addCostCenter.save')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
      {/* Add Project Modal */}
      {showAddProjectModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddProjectModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.title', { defaultValue: 'إضافة مشروع جديد' })}</h2>
              <button
                onClick={() => setShowAddProjectModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddProject}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={projectForm.name_ar}
                    onChange={handleProjectFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={projectForm.name_en}
                    onChange={handleProjectFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.code', { defaultValue: 'كود المشروع' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={projectForm.code}
                    onChange={handleProjectFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>

                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.customer', { defaultValue: 'العميل' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddCustomerModal(true); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setTaxDropdownOpen(false);
                      setCostCenterDropdownOpen(false);
                      setProjModalCustomerDropdownOpen((p) => !p);
                    }}
                    style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {projectForm.customer_id ? (customers || []).find(c => String(c.id) === String(projectForm.customer_id)) ? `${(customers || []).find(c => String(c.id) === String(projectForm.customer_id)).code ? `${(customers || []).find(c => String(c.id) === String(projectForm.customer_id)).code} - ` : ''}${isRTL ? ((customers || []).find(c => String(c.id) === String(projectForm.customer_id)).name_ar || (customers || []).find(c => String(c.id) === String(projectForm.customer_id)).name_en) : ((customers || []).find(c => String(c.id) === String(projectForm.customer_id)).name_en || (customers || []).find(c => String(c.id) === String(projectForm.customer_id)).name_ar)}` : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' }) : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' })}
                    </span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {projModalCustomerDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={projModalCustomerDropdownOpen}
                      onToggle={setProjModalCustomerDropdownOpen}
                      searchTerm={projModalCustomerSearchTerm}
                      onSearchChange={(val) => {
                        setProjModalCustomerSearchTerm(val);
                        if (projModalCustomerSearchTimeoutRef.current) clearTimeout(projModalCustomerSearchTimeoutRef.current);
                        projModalCustomerSearchTimeoutRef.current = setTimeout(() => {
                          setProjModalCustomerCurrentPage(1);
                          dispatch(getCustomers({ page: 1, per_page: 10, search: val || undefined }));
                        }, 300);
                      }}
                      searchInputRef={projModalCustomerSearchInputRef}
                      items={Array.isArray(customers) ? customers : []}
                      pagination={customersPagination}
                      currentPage={projModalCustomerCurrentPage}
                      onPageChange={(page) => {
                        setProjModalCustomerCurrentPage(page);
                        dispatch(getCustomers({ page, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
                      }}
                      onSelect={(id) => {
                        setProjectForm((prev) => ({ ...prev, customer_id: id }));
                        setProjModalCustomerDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.customer', { defaultValue: 'عميل' })}
                    />
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.startDate', { defaultValue: 'تاريخ البداية' })}</label>
                  <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.endDate', { defaultValue: 'تاريخ النهاية' })}</label>
                  <input type="date" name="end_date" value={projectForm.end_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.status', { defaultValue: 'الحالة' })}</label>
                  <select name="status" value={projectForm.status} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    <option value="planned">{t('quotations.add.modals.addProject.statusOptions.planned', { defaultValue: 'مخطط' })}</option>
                    <option value="in_progress">{t('quotations.add.modals.addProject.statusOptions.inProgress', { defaultValue: 'قيد التنفيذ' })}</option>
                    <option value="paused">{t('quotations.add.modals.addProject.statusOptions.paused', { defaultValue: 'متوقف' })}</option>
                    <option value="completed">{t('quotations.add.modals.addProject.statusOptions.completed', { defaultValue: 'مكتمل' })}</option>
                    <option value="cancelled">{t('quotations.add.modals.addProject.statusOptions.cancelled', { defaultValue: 'ملغى' })}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.contractValue', { defaultValue: 'قيمة العقد' })}</label>
                  <input type="number" name="contract_value" value={projectForm.contract_value} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>

                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddTaxModal(true); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setProjModalCustomerDropdownOpen(false);
                      setCostCenterDropdownOpen(false);
                      setTaxDropdownOpen((p) => !p);
                    }}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span>{projectForm.tax_id ? (taxes || []).find(t => t.id === projectForm.tax_id)?.name_ar || (taxes || []).find(t => t.id === projectForm.tax_id)?.name_en : t('quotations.add.selectTax', { defaultValue: 'اختر الضريبة' })}</span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {taxDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={taxDropdownOpen}
                      onToggle={setTaxDropdownOpen}
                      searchTerm={taxSearchTerm}
                      onSearchChange={setTaxSearchTerm}
                      searchInputRef={taxSearchInputRef}
                      items={taxes || []}
                      pagination={taxesPagination}
                      currentPage={taxCurrentPage}
                      onPageChange={setTaxCurrentPage}
                      onSelect={(id) => {
                        setProjectForm(prev => ({ ...prev, tax_id: id }));
                        setTaxDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}
                    />
                  )}
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddCostCenterModal(true); setCostCenterSource('project'); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setProjModalCustomerDropdownOpen(false);
                      setTaxDropdownOpen(false);
                      setCostCenterDropdownOpen((p) => !p);
                    }}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span>{projectForm.cost_center_id ? `${(costCenters || []).find(c => c.id === projectForm.cost_center_id)?.code ? `${(costCenters || []).find(c => c.id === projectForm.cost_center_id)?.code} - ` : ''}${isRTL ? ((costCenters || []).find(c => c.id === projectForm.cost_center_id)?.name_ar) : ((costCenters || []).find(c => c.id === projectForm.cost_center_id)?.name_en || (costCenters || []).find(c => c.id === projectForm.cost_center_id)?.name_ar)}` : t('quotations.add.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' })}</span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {costCenterDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={costCenterDropdownOpen}
                      onToggle={setCostCenterDropdownOpen}
                      searchTerm={costCenterSearchTerm}
                      onSearchChange={setCostCenterSearchTerm}
                      searchInputRef={costCenterSearchInputRef}
                      items={costCenters || []}
                      pagination={costCentersPagination}
                      currentPage={costCenterCurrentPage}
                      onPageChange={setCostCenterCurrentPage}
                      onSelect={(id) => {
                        setProjectForm(prev => ({ ...prev, cost_center_id: id }));
                        setCostCenterDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                    />
                  )}
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('quotations.add.modals.addProject.notes', { defaultValue: 'ملاحظات' })}
                </label>
                <textarea
                  ref={projectNotesTextareaRef}
                  name="notes"
                  value={projectForm.notes}
                  onChange={handleProjectFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddProjectModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingProject}
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingProject ? 'not-allowed' : 'pointer', opacity: isSubmittingProject ? 0.7 : 1 }}
                >
                  {isSubmittingProject ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Tax Modal */}
      {showAddTaxModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000000,
            padding: '20px'
          }}
          onClick={() => setShowAddTaxModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                {t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}
              </h2>
              <button
                onClick={() => setShowAddTaxModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddTax}>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={taxForm.name_ar}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'rtl',
                      textAlign: 'right'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={taxForm.name_en}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.rate', { defaultValue: 'النسبة (%)' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="rate"
                    value={taxForm.rate}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={taxForm.is_active}
                    onChange={(e) => setTaxForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                    {t('quotations.add.modals.addTax.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddTaxModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000000,
            padding: '20px'
          }}
          onClick={() => setShowAddCustomerModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                {t('customers.add.title', { defaultValue: 'إضافة عميل جديد' })}
              </h2>
              <button
                onClick={() => setShowAddCustomerModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCustomer}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={customerForm.name_ar}
                    onChange={handleCustomerFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'rtl',
                      textAlign: 'right'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={customerForm.name_en}
                    onChange={handleCustomerFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.code', { defaultValue: 'كود العميل' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={customerForm.code}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.contactPerson', { defaultValue: 'الشخص المسؤول' })}
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={customerForm.contact_person}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.email', { defaultValue: 'البريد الإلكتروني' })}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={customerForm.email}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.phone', { defaultValue: 'رقم الهاتف' })}
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={customerForm.phone}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.taxNumber', { defaultValue: 'الرقم الضريبي' })}
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    value={customerForm.tax_number}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={customerForm.is_active}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                    {t('customers.add.status.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('customers.add.address', { defaultValue: 'العنوان' })}
                </label>
                <input
                  type="text"
                  name="address"
                  value={customerForm.address}
                  onChange={handleCustomerFormChange}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('customers.add.notes', { defaultValue: 'ملاحظات' })}
                </label>
                <textarea
                  name="notes"
                  value={customerForm.notes}
                  onChange={handleCustomerFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddCustomerModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingCustomer}
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingCustomer ? 'not-allowed' : 'pointer', opacity: isSubmittingCustomer ? 0.7 : 1 }}
                >
                  {isSubmittingCustomer ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

/* Dropdown Component */
const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, displayValue, placeholder, label, renderItem }) => {
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';

  const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = isRTL
      ? (item[nameArField] || item[nameEnField] || '')
      : (item[nameEnField] || item[nameArField] || '');
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const internalInputRef = useRef(null);
  const inputRef = searchInputRef || internalInputRef;

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus({ preventScroll: true });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, inputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
      <div style={{ padding: 8 }}>
        <input
          ref={inputRef}
          type="text"
          placeholder={`${t('quotations.add.search', { defaultValue: 'ابحث عن' })} ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              inputRef.current?.focus({ preventScroll: true });
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }}
        />
      </div>
      {pagination && pagination.last_page >= 1 && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('common.pagination.page', { defaultValue: 'الصفحة' })} {pagination.current_page} {t('common.pagination.of', { defaultValue: 'من' })} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div>
        {(Array.isArray(items) ? items : []).map((item) => (
          <div
            key={item.id}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelect(item.id);
            }}
            style={{
              padding: '12px 15px',
              cursor: 'pointer',
              borderTop: '1px solid #333',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'white',
              fontSize: '14px',
              transition: 'background-color 0.2s',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#14263b'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span style={{ color: 'white' }}>
              {renderItem ? renderItem(item) : renderDropdownItemLabel(item, 'code', 'name_ar', 'name_en')}
            </span>
          </div>
        ))}
        {(!Array.isArray(items) || items.length === 0) && <div style={{ padding: 12, textAlign: 'center', color: '#bbb', direction: isRTL ? 'rtl' : 'ltr' }}>{t('quotations.add.noResults', { defaultValue: 'لا توجد نتائج' })}</div>}
      </div>
    </div>
  );
};

export default AddPaymentVoucher;